import { useSyncExternalStore } from 'react';

import { logger, monitoring } from 'common/services';

const PIP_WIDTH = 320;
const PIP_HEIGHT = 480;

/**
 * How the window was opened: on its own when the share started, by the host from the toast, the
 * Present menu or the browser's own media controls, or by Chrome when the host left the tab
 * (`startAutoShow`). Split so RUM shows which route Firefox hosts, who never get `auto` or
 * `tab_left`, actually find.
 */
export type PipTrigger = 'auto' | 'toast' | 'menu' | 'media_control' | 'tab_left';

/** Opens the host asked for: each turns showing on a tab switch back on after they closed one. */
const HOST_TRIGGERS: readonly PipTrigger[] = ['toast', 'menu', 'media_control'];

/**
 * Why the window closed. A close the code did not explain (`closeScreenSharePip`) is the host's own:
 * the window's close button, or leaving the page. `tab_returned` is the hide when the host comes
 * back to this tab, ours or Chrome's.
 */
export type PipCloseReason = 'host' | 'share_ended' | 'crashed' | 'tab_returned';

/**
 * The one floating window this tab may hold. The browser allows a single Document Picture-in-Picture
 * window per tab, so module state mirrors that limit rather than pretending each caller owns one.
 */
let current: { window: Window; trigger: PipTrigger; openedAt: number } | null = null;
let pendingOpen: Promise<Window | null> | null = null;
/**
 * Bumped by every close, so an open that was in flight when the share ended knows it is stale. A
 * counter rather than a flag: a new share can start before the stale request settles, and must get
 * a request of its own rather than reuse the cancelled one.
 */
let openGeneration = 0;
/**
 * Set when the host closes the window by hand, or it crashes: leaving the tab stops bringing it back
 * for the rest of the share, until they reopen it from the toast or the Present menu.
 */
let autoShowOptedOut = false;
/** Leaving the tab brings the window back this share (`startAutoShow`); the window's tip reads this. */
let autoShowActive = false;
/** The browser has opened the window on a tab switch, so the host has met its permission prompt. */
let hasAutoShown = false;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => current?.window ?? null;

const setAutoShowActive = (active: boolean) => {
  if (autoShowActive === active) return;
  autoShowActive = active;
  notify();
};

/**
 * How long after leaving the tab the window must have appeared before Chrome counts as not showing
 * it. Opening it takes Chrome a fraction of this; the window holds Chrome's permission prompt while
 * the host decides, so an unanswered prompt still counts as shown.
 */
const AUTO_SHOW_MISSED_MS = 1500;

/**
 * Forgets the window and records why it went, once per window: from `closeScreenSharePip` with the
 * code's reason, or from `pagehide` as the host's own close if the code did not explain it first.
 * How long hosts keep it, and whether they close it themselves, says whether it is useful.
 */
const recordClosed = (reason: PipCloseReason) => {
  if (!current) return;
  const { trigger, openedAt } = current;
  current = null;
  monitoring.trackAction('broadcast.screen_share_pip.closed', {
    trigger,
    reason,
    openForMs: Math.round(performance.now() - openedAt),
  });
  notify();
};

export const isDocumentPipSupported = () => !!window.documentPictureInPicture;

/**
 * A Document PiP window opens as an empty document. Link tags are cloned as-is; style tags are
 * re-serialised from their CSSOM, because a production emotion sheet inserts rules with `insertRule`
 * and leaves its text node empty. Emotion rules added AFTER this copy are handled by the PiP-scoped
 * emotion cache in `ScreenSharePip`, not here.
 */
const copyDocumentStyles = (target: Document) => {
  // The PiP document has no URL of its own, so root-relative font and image URLs need a base.
  const base = target.createElement('base');
  base.href = window.location.origin;
  target.head.append(base);

  document.head.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
    if (node instanceof HTMLLinkElement) {
      target.head.append(node.cloneNode(true));
      return;
    }

    const style = target.createElement('style');
    try {
      const rules = (node as HTMLStyleElement).sheet?.cssRules;
      style.textContent = rules ? Array.from(rules, (rule) => rule.cssText).join('\n') : node.textContent;
    } catch (error) {
      // A cross-origin sheet hides its rules; fall back to the tag's text, and leave a trace.
      logger.warn('[ScreenSharePip] Could not read a stylesheet into the floating window', { error });
      style.textContent = node.textContent;
    }
    target.head.append(style);
  });

  // `visual-refresh` lives on <html> and switches the global font and tokens.
  target.documentElement.className = document.documentElement.className;
  target.body.style.margin = '0';
};

/**
 * Opens the floating window. Must run inside a user activation: a click, or the moment the
 * screen-share picker resolves (approving `getDisplayMedia` grants one in Chromium). Resolves to
 * `null` when the browser refuses, so the caller can offer it another way (the toast, and the Present
 * menu's "Open floating window" row).
 */
export const openScreenSharePip = (trigger: PipTrigger): Promise<Window | null> => {
  if (current) return Promise.resolve(current.window);
  if (pendingOpen) return pendingOpen;

  const api = window.documentPictureInPicture;
  if (!api) {
    monitoring.trackAction('broadcast.screen_share_pip.unsupported');
    return Promise.resolve(null);
  }

  const generation = openGeneration;
  const request: Promise<Window | null> = api
    .requestWindow({ width: PIP_WIDTH, height: PIP_HEIGHT })
    .then((opened) => {
      if (generation !== openGeneration) {
        opened.close();
        return null;
      }
      copyDocumentStyles(opened.document);
      opened.addEventListener('pagehide', () => {
        if (current?.window !== opened) return;
        // Chrome closes a window it opened on a tab switch once the tab is back in view. That is not
        // the host closing it, so it must not stop the next one.
        if (current.trigger === 'tab_left' && document.visibilityState === 'visible') {
          recordClosed('tab_returned');
          return;
        }
        autoShowOptedOut = true;
        recordClosed('host');
      });
      if (HOST_TRIGGERS.includes(trigger)) autoShowOptedOut = false;
      if (trigger === 'tab_left') hasAutoShown = true;
      current = { window: opened, trigger, openedAt: performance.now() };
      monitoring.trackAction('broadcast.screen_share_pip.opened', { trigger });
      notify();
      return opened;
    })
    .catch((error: unknown) => {
      // Expected when there was no click to open from (NotAllowedError, always in Firefox); the toast
      // and the Present menu cover it.
      logger.warn('[ScreenSharePip] Could not open the floating window', { trigger, error });
      monitoring.trackAction('broadcast.screen_share_pip.blocked', {
        trigger,
        reason: error instanceof Error ? error.name : 'unknown',
      });
      return null;
    })
    .finally(() => {
      if (pendingOpen === request) pendingOpen = null;
    });

  pendingOpen = request;
  return request;
};

/** Also cancels an open still in flight, so a share that ends first never leaves a window behind. */
export const closeScreenSharePip = (reason: Exclude<PipCloseReason, 'host'>) => {
  openGeneration += 1;
  pendingOpen = null;
  // A window that crashed would crash again on every tab switch.
  if (reason === 'crashed') autoShowOptedOut = true;
  const closing = current?.window;
  recordClosed(reason);
  closing?.close();
};

/**
 * Meet-style, for one share: the window hides when the host comes back to this tab and shows again
 * when they leave it. Returns the cleanup.
 *
 * Showing again relies on Chrome's automatic picture-in-picture (Chrome 120+): a tab switch fires the
 * media session's `enterpictureinpicture` action, which may open the window without a click, and Chrome
 * closes that window itself when the tab is back in view. Chrome only does this for a page capturing
 * the camera or microphone, so `isCapturing` is asked before each hide. Where the window could not come
 * back (another browser, or no live mic or camera), it does not hide either: a window that vanished
 * for good would leave the host hunting for the Present menu.
 *
 * A muted mic still counts: `useAudio` mutes with `setMuted`, which keeps the capture running.
 *
 * Chrome asks the host's permission the first time, and a page can neither ask earlier nor read the
 * answer. So when the window does not appear after the host leaves (they chose "Don't allow"), it
 * stops hiding for the rest of the share, and `onMissed` runs once they are back, to offer it again.
 */
export const startAutoShow = ({
  isCapturing,
  onMissed,
}: {
  isCapturing: () => boolean;
  onMissed: () => void;
}): (() => void) => {
  autoShowOptedOut = false;
  if (!isDocumentPipSupported() || !('mediaSession' in navigator)) return () => {};

  try {
    navigator.mediaSession.setActionHandler('enterpictureinpicture', ({ enterPictureInPictureReason } = {}) => {
      // The same action fires when the host picks picture-in-picture from the browser's own media
      // controls, tab still in view. That is the host asking for it, like the Present menu, so it opens
      // even after they closed one, and their next close is theirs, not Chrome hiding it on return.
      // Without a reason (before Chrome 142), a tab still in view says it was the host.
      const isMediaControl =
        enterPictureInPictureReason === 'useraction' ||
        (enterPictureInPictureReason !== 'contentoccluded' && document.visibilityState !== 'hidden');
      if (isMediaControl) void openScreenSharePip('media_control');
      else if (!autoShowOptedOut) void openScreenSharePip('tab_left');
    });
  } catch (error) {
    // Before Chrome 120, and in Firefox, the action is not a known value and this throws a TypeError.
    logger.info('[ScreenSharePip] Automatic picture-in-picture is not available', { error });
    return () => {};
  }
  setAutoShowActive(true);

  let missTimer: ReturnType<typeof setTimeout> | undefined;
  // Chrome missed a show, and the host is not back yet to be offered it: `onMissed` runs once, on return.
  let offerPending = false;

  const onVisibilityChange = () => {
    clearTimeout(missTimer);

    if (document.visibilityState === 'hidden') {
      // With no window open, Chrome should open one now. A host who closed it asked for none.
      if (!autoShowActive || current || autoShowOptedOut || !isCapturing()) return;
      missTimer = setTimeout(() => {
        if (document.visibilityState !== 'hidden' || current || pendingOpen) return;
        offerPending = true;
        setAutoShowActive(false);
        monitoring.trackAction('broadcast.screen_share_pip.auto_show_missed');
      }, AUTO_SHOW_MISSED_MS);
      return;
    }

    if (offerPending) {
      offerPending = false;
      if (!current) onMissed();
      return;
    }
    if (autoShowActive && current && isCapturing()) closeScreenSharePip('tab_returned');
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    clearTimeout(missTimer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    navigator.mediaSession.setActionHandler('enterpictureinpicture', null);
    setAutoShowActive(false);
  };
};

/** Reads one value of this module's state, re-rendering when `notify` reports it changed. */
const usePipState = <T>(read: () => T, serverValue: T) => useSyncExternalStore(subscribe, read, () => serverValue);

export const usePipWindow = () => usePipState(getSnapshot, null);

/** Whether leaving the tab brings the window back, this share. */
export const useIsAutoShowActive = () => usePipState(() => autoShowActive, false);

/** Whether the browser has opened the window on a tab switch yet, on this page. */
export const useHasAutoShown = () => usePipState(() => hasAutoShown, false);
